export default class {

    constructor (component) {
        this.#component = component;

        this.#component.styleSelectors.add(this.constructor.surfaceBaseStyleSelector);
        this.#component.events.addEvents(this.constructor.surfaceBaseEventSettings);
        this.#component.attachProps(this.constructor.surfaceBaseProps, this.surfaceBasePropCallbacks);

        const isLevelOveridden = this.#component.level !== undefined;

        if (!isLevelOveridden) {
            this.#autoPopulateLevel();
        }

        const observer = new MutationObserver((mutationArray) => {
            const isNodeAdded = mutationArray.length && mutationArray[0].addedNodes?.length;

            if (isNodeAdded) {
                this.#component.events.LevelChange.dispatch({userDefinedLevel: this.#userDefinedLevel}, true);
            }
        });

        observer.observe(this.#component, {
            subtree: false,
            childList: true,
        });
    }

    // Static

    static surfaceBaseStyleSelector = 'v2';
    static initialisationProperty = 'surface';
    static levels = [0, 1, 2, 3];
    static maxLevel = this.levels[this.levels.length - 1];
    static defaultLevel = this.levels[1];

    static get surfaceBaseEventSettings () {
        return {
            subscriptions: {
                'theme-change': this.prototype.updateThemeFromEvent,
                'level-change': this.prototype.updateLevelFromEvent
            },
            events: {
                ThemeChange: 'theme-change',
                LevelChange: 'level-change'
            }
        };
    }

    static get surfaceBaseProps () {
        return [
            new wealthify.Prop('theme', {
                default: wealthify.enums.themes.Light,
                type: wealthify.propTypes.ArrayItem(Object.values(wealthify.enums.themes))
            }),
            new wealthify.Prop('level', {
                type: wealthify.propTypes.Number(),
                validator: wealthify.propValidators.IsInArray(this.constructor.levels)
            })
        ];
    }

    // Public

    get surfaceBasePropCallbacks () {
        return {
            theme: () => this.#updateTheme(),
            level: () => this.#updateLevel()
        };
    }

    updateThemeFromEvent (event) {
        this.theme = event.detail?.theme;
    }

    updateLevelFromEvent (event) {
        this.level = event.detail?.userDefinedLevel;
    }

    // Private

    #component = null;
    #userDefinedLevel = undefined;

    #updateTheme () {
        this.#toggleThemeClass();
        this.#component.events.ThemeChange.dispatch({theme: this.#component.theme}, true);
    }

    #toggleThemeClass (theme = this.#component.theme) {
        Object.values(wealthify.enums.themes).forEach((theme) => {
            this.#component.dom.root.classList.remove(`th--${theme}`);
        });

        this.#component.dom.root.classList.add(`th--${theme}`);
    }

    #updateLevel () {
        Object.values(this.constructor.levels).forEach((level) => {
            this.#component.classList.remove(`${this.#component.styleSelector}__elevation--${level}`);
        });

        this.#component.classList.add(`${this.#component.styleSelector}__elevation--${this.#component.level}`);
        // Set user defined level as this is the only method called on user definition of method
        this.#userDefinedLevel = this.#component.level;
    }

    #autoPopulateLevel () {
        const parent = wealthify.dom.getClosestMatchingParent(this.#component, this.#component.tagName.toLowerCase());
        const shouldIncrement = parent && parent.type === this.#component.type;
        const level = shouldIncrement
            ? Math.min(parent.level + 1, this.constructor.maxLevel)
            : this.constructor.defaultLevel;


        this.#component.level = level;
        // Reset user defined level to indicate that level is incremented from parent
        this.#userDefinedLevel = undefined;
    }
}
